/**
 * Settings (owner). Gym profile, locations, fiscal devices (reads), plus
 * appearance + language preferences. Read-only surfaces are RLS-scoped.
 */
import { useQuery } from '@tanstack/react-query';
import { Building2, Cpu, MapPin, Monitor, Moon, Sun } from 'lucide-react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useTheme } from '../../app/ThemeProvider.js';
import { Card } from '../../components/ui/Card.js';
import { Skeleton } from '../../components/ui/Feedback.js';
import { PageHeader } from '../../components/ui/Layout.js';
import { StatusPill } from '../../components/ui/StatusPill.js';
import { SegmentedControl } from '../../components/ui/Tabs.js';
import { useAuth } from '../../lib/auth.js';
import { supabase } from '../../lib/supabase.js';

interface Tenant {
  name: string;
  legal_name: string | null;
  tax_id: string | null;
  slug: string;
  status: string;
}
interface Loc {
  id: string;
  name: string;
  address: string | null;
  capacity: number | null;
  is_active: boolean;
}
interface Device {
  id: string;
  label: string | null;
  driver: string;
  serial_number: string | null;
  is_active: boolean;
}

function InfoRow({ label, value }: { label: ReactNode; value: ReactNode }): ReactNode {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5">
      <span className="text-sm text-muted">{label}</span>
      <span className="text-sm font-medium text-fg">{value}</span>
    </div>
  );
}

function SectionCard({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof Building2;
  title: ReactNode;
  children: ReactNode;
}): ReactNode {
  return (
    <Card className="p-5">
      <div className="mb-3 flex items-center gap-2">
        <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary/15 text-primary">
          <Icon className="h-4 w-4" aria-hidden />
        </span>
        <h3 className="font-display text-base font-semibold text-fg">{title}</h3>
      </div>
      {children}
    </Card>
  );
}

export function SettingsPage(): ReactNode {
  const { t, i18n } = useTranslation();
  const { mode, setMode } = useTheme();
  const { claims } = useAuth();
  const tenantId = claims?.tenant_id ?? '';

  const tenant = useQuery({
    queryKey: ['tenant', tenantId],
    enabled: !!tenantId,
    queryFn: async (): Promise<Tenant | null> => {
      const { data, error } = await supabase
        .from('tenants')
        .select('name, legal_name, tax_id, slug, status')
        .eq('id', tenantId)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as Tenant) ?? null;
    },
  });
  const locations = useQuery({
    queryKey: ['settings_locations', tenantId],
    enabled: !!tenantId,
    queryFn: async (): Promise<Loc[]> => {
      const { data, error } = await supabase
        .from('locations')
        .select('id, name, address, capacity, is_active')
        .order('name');
      if (error) throw error;
      return (data ?? []) as unknown as Loc[];
    },
  });
  const devices = useQuery({
    queryKey: ['settings_devices', tenantId],
    enabled: !!tenantId,
    queryFn: async (): Promise<Device[]> => {
      const { data, error } = await supabase
        .from('fiscal_devices')
        .select('id, label, driver, serial_number, is_active')
        .order('created_at');
      if (error) throw error;
      return (data ?? []) as unknown as Device[];
    },
  });

  return (
    <div className="grid gap-5">
      <PageHeader title={t('nav.settings')} subtitle={t('settings.subtitle')} />

      <div className="grid gap-4 lg:grid-cols-2">
        <SectionCard icon={Building2} title={t('settings.gymProfile')}>
          {tenant.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <div className="divide-y divide-border">
              <InfoRow label={t('settings.gymName')} value={tenant.data?.name ?? '—'} />
              <InfoRow label={t('settings.legalName')} value={tenant.data?.legal_name ?? '—'} />
              <InfoRow label={t('settings.taxId')} value={tenant.data?.tax_id ?? '—'} />
              <InfoRow
                label={t('settings.accountStatus')}
                value={
                  <StatusPill
                    status={tenant.data?.status === 'active' ? 'active' : 'pending'}
                    label={tenant.data?.status ?? '—'}
                  />
                }
              />
            </div>
          )}
        </SectionCard>

        <SectionCard icon={Monitor} title={t('settings.appearance')}>
          <div className="divide-y divide-border">
            <div className="flex items-center justify-between gap-3 py-3">
              <span className="text-sm text-muted">{t('settings.theme')}</span>
              <SegmentedControl
                value={mode}
                onChange={setMode}
                options={[
                  {
                    value: 'dark',
                    label: (
                      <span className="inline-flex items-center gap-1.5">
                        <Moon className="h-3.5 w-3.5" />
                        {t('settings.dark')}
                      </span>
                    ),
                  },
                  {
                    value: 'light',
                    label: (
                      <span className="inline-flex items-center gap-1.5">
                        <Sun className="h-3.5 w-3.5" />
                        {t('settings.light')}
                      </span>
                    ),
                  },
                ]}
              />
            </div>
            <div className="flex items-center justify-between gap-3 py-3">
              <span className="text-sm text-muted">{t('settings.language')}</span>
              <SegmentedControl
                value={(i18n.language?.slice(0, 2) as 'mk' | 'sq' | 'en') ?? 'mk'}
                onChange={(v) => void i18n.changeLanguage(v)}
                options={[
                  { value: 'mk', label: 'MK' },
                  { value: 'sq', label: 'SQ' },
                  { value: 'en', label: 'EN' },
                ]}
              />
            </div>
          </div>
        </SectionCard>

        <SectionCard icon={MapPin} title={t('settings.locations')}>
          {locations.isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : (locations.data ?? []).length === 0 ? (
            <p className="text-sm text-muted">{t('settings.noLocations')}</p>
          ) : (
            <div className="divide-y divide-border">
              {(locations.data ?? []).map((l) => (
                <div key={l.id} className="flex items-center justify-between gap-3 py-2.5">
                  <div>
                    <p className="text-sm font-medium text-fg">{l.name}</p>
                    {l.address ? <p className="text-xs text-muted">{l.address}</p> : null}
                  </div>
                  <StatusPill
                    status={l.is_active ? 'active' : 'cancelled'}
                    label={
                      l.capacity
                        ? t('settings.capacityN', { n: l.capacity })
                        : l.is_active
                          ? t('common.active')
                          : t('common.inactive')
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard icon={Cpu} title={t('settings.fiscalDevices')}>
          {devices.isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : (devices.data ?? []).length === 0 ? (
            <p className="text-sm text-muted">{t('settings.noDevices')}</p>
          ) : (
            <div className="divide-y divide-border">
              {(devices.data ?? []).map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-3 py-2.5">
                  <div>
                    <p className="text-sm font-medium text-fg">
                      {d.label ?? d.serial_number ?? d.id.slice(0, 8)}
                    </p>
                    <p className="text-xs text-muted">{d.driver}</p>
                  </div>
                  <StatusPill
                    status={d.is_active ? 'active' : 'cancelled'}
                    label={d.is_active ? t('common.active') : t('common.inactive')}
                  />
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
